/**
 * autoLayout.ts - Auto-layout utilities for adding nodes to the automation flow
 *
 * Provides functions to add nodes with automatic positioning using the Dagre
 * layout engine. These functions ensure nodes are properly positioned without
 * overlaps after any addition.
 *
 * Related:
 *   - layoutEngine.ts - Core Dagre layout implementation
 *   - layoutConstants.ts - Spacing constants
 */

import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import type { ChainTerminus } from '../hooks/useChainTermini';
import { applyLayout } from './layoutEngine';

/**
 * Adds a new node to the flow and applies auto-layout to all nodes.
 *
 * If connectToNodeId is provided, creates an edge from that node to the new node.
 * The entire graph is then re-laid out using Dagre to prevent overlaps.
 *
 * @param nodes - Existing nodes in the flow
 * @param edges - Existing edges in the flow
 * @param newNode - The new node to add
 * @param connectToNodeId - Optional node ID to connect from
 * @param sourceHandle - Optional source handle for the connection (for switch nodes)
 * @returns Updated nodes and edges with layout applied
 *
 * @example
 * ```typescript
 * const result = addNodeWithLayout(nodes, edges, newEmailNode, 'trigger');
 * setNodes(result.nodes);
 * setEdges(result.edges);
 * ```
 */
export function addNodeWithLayout(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	newNode: Node<BaseNodeData>,
	connectToNodeId?: string,
	sourceHandle?: string
): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	// Add the new node to the array
	const updatedNodes = [...nodes, newNode];

	// Create edge if connection is specified
	let updatedEdges = [...edges];
	if (connectToNodeId) {
		const newEdge: Edge = {
			id: `edge-${connectToNodeId}-${newNode.id}`,
			source: connectToNodeId,
			target: newNode.id,
			sourceHandle: sourceHandle ?? null,
			type: 'default',
		};
		updatedEdges = [...updatedEdges, newEdge];
	}

	// Apply layout to position all nodes
	const layoutResult = applyLayout(updatedNodes, updatedEdges);

	return layoutResult;
}

/**
 * Inserts a node after a specific target node in the flow.
 *
 * Creates an edge from the target node to the new node and applies layout.
 * This is the primary function for the chain terminus "+" button workflow.
 *
 * @param nodes - Existing nodes in the flow
 * @param edges - Existing edges in the flow
 * @param targetNodeId - The node ID after which to insert the new node
 * @param newNode - The new node to insert
 * @param sourceHandle - Optional source handle for switch node branches
 * @returns Updated nodes and edges with layout applied
 *
 * @example
 * ```typescript
 * const result = insertNodeAfter(nodes, edges, 'step-1', newEmailNode);
 * setNodes(result.nodes);
 * setEdges(result.edges);
 * ```
 */
export function insertNodeAfter(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	targetNodeId: string,
	newNode: Node<BaseNodeData>,
	sourceHandle?: string
): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	// Verify target node exists
	const targetNode = nodes.find(n => n.id === targetNodeId);
	if (!targetNode) {
		throw new Error(`Target node "${targetNodeId}" not found`);
	}

	// Use addNodeWithLayout to add the node and create the connection
	return addNodeWithLayout(nodes, edges, newNode, targetNodeId, sourceHandle);
}

/**
 * Finds the closest chain terminus to a given drop position.
 *
 * Used during drag-and-drop to determine which branch endpoint the user
 * is targeting. The closest terminus is determined by Euclidean distance.
 *
 * @param dropPosition - The position where the user dropped the node
 * @param termini - Array of chain terminus positions from useChainTermini
 * @returns The closest ChainTerminus, or null if no termini exist
 *
 * @example
 * ```typescript
 * const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
 * const closest = findClosestTerminus(position, termini);
 * if (closest) {
 *   insertNodeAfter(nodes, edges, closest.nodeId, newNode);
 * }
 * ```
 */
export function findClosestTerminus(
	dropPosition: { x: number; y: number },
	termini: ChainTerminus[]
): ChainTerminus | null {
	if (termini.length === 0) {
		return null;
	}

	let closest: ChainTerminus | null = null;
	let minDistance = Infinity;

	for (const terminus of termini) {
		const dx = dropPosition.x - terminus.position.x;
		const dy = dropPosition.y - terminus.position.y;
		const distance = Math.sqrt(dx * dx + dy * dy);

		if (distance < minDistance) {
			minDistance = distance;
			closest = terminus;
		}
	}

	return closest;
}

/**
 * Finds the entry node of a pasted group — the node with no incoming edge
 * from other pasted nodes. For a single-node paste this is just that node.
 *
 * @param pasteResult - The result from pasteAtPosition containing new nodes and internal edges
 * @returns The ID of the topological root node, or null if the group is empty
 */
export function findPastedEntryNodeId(pasteResult: { nodes: Node<BaseNodeData>[]; edges: Edge[] }): string | null {
	if (pasteResult.nodes.length === 0) {
		return null;
	}

	if (pasteResult.nodes.length === 1) {
		return pasteResult.nodes[0].id;
	}

	// Collect IDs that appear as an internal-edge target — those nodes have a predecessor inside the pasted group.
	const pastedNodeIds = new Set(pasteResult.nodes.map(n => n.id));
	const nodesWithInternalIncoming = new Set<string>();

	for (const edge of pasteResult.edges) {
		if (pastedNodeIds.has(edge.source) && pastedNodeIds.has(edge.target)) {
			nodesWithInternalIncoming.add(edge.target);
		}
	}

	// The entry node is the one without an internal incoming edge.
	for (const node of pasteResult.nodes) {
		if (!nodesWithInternalIncoming.has(node.id)) {
			return node.id;
		}
	}

	// Fallback: return the first node (should not happen with a valid DAG).
	return pasteResult.nodes[0].id;
}

/**
 * Inserts pasted/duplicated nodes at a chain terminus.
 *
 * Creates a connecting edge from the terminus node to the entry node of the
 * pasted group, merges everything into the existing graph, and runs Dagre
 * layout so positions are computed from topology — mirroring the drag-and-drop
 * insertion path.
 *
 * @param pasteResult  - Nodes and internal edges produced by pasteAtPosition
 * @param terminus     - The chain terminus to attach the pasted group to
 * @param currentNodes - Existing nodes already on the canvas
 * @param currentEdges - Existing edges already on the canvas
 * @returns Fully laid-out nodes and edges (replaces the entire graph)
 */
export function insertPastedNodesAtTerminus(
	pasteResult: { nodes: Node<BaseNodeData>[]; edges: Edge[] },
	terminus: ChainTerminus,
	currentNodes: Node<BaseNodeData>[],
	currentEdges: Edge[]
): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	const entryNodeId = findPastedEntryNodeId(pasteResult);

	// Build the connecting edge from the terminus to the pasted entry node.
	// When no entry node is found, skip the connecting edge and just merge.
	const extraEdges: Edge[] = [];
	if (entryNodeId) {
		// Chain termini are always single-output nodes (steps/segments).
		// Do NOT use switch-case handles here; branchInfo only indicates *which branch*
		// a node belongs to, not that the terminus is a switch.
		extraEdges.push({
			id: `edge-${terminus.nodeId}-${entryNodeId}`,
			source: terminus.nodeId,
			target: entryNodeId,
			sourceHandle: null,
			type: 'default',
		});
	}

	const mergedNodes = [...currentNodes, ...pasteResult.nodes];
	const mergedEdges = [...currentEdges, ...pasteResult.edges, ...extraEdges];

	return applyLayout(mergedNodes, mergedEdges);
}
